import type {
  SettingsColumnFormat,
  SettingsFormField,
  SettingsFormValue,
  SettingsFormValues,
  SettingsRecord,
  TdsApplyChange,
} from './types';
import { doctorDisplayName } from '../../lib/doctor-name';
import { formatDate } from '../../lib/datetime';
import { isPhoneFieldName, normalizePhoneInput } from '../../lib/phone-input';
import { stripHtml } from '../../lib/rich-text';

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const getValueAtPath = (source: unknown, path: string): unknown => {
  let current = source;
  for (const segment of path.split('.')) {
    if (Array.isArray(current)) {
      const index = Number(segment);
      if (!Number.isInteger(index)) return undefined;
      current = current[index];
    } else if (isObject(current)) {
      current = current[segment];
    } else {
      return undefined;
    }
  }
  return current;
};

const toFormValue = (field: SettingsFormField, value: unknown): SettingsFormValue => {
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (typeof item === 'string') return item;
      if (!isObject(item)) return '';
      const referenceId = item.id ?? item._id;
      return typeof referenceId === 'string' ? referenceId : '';
    }).filter(Boolean);
  }
  if (isObject(value)) {
    const referenceId = value.id ?? value._id;
    return typeof referenceId === 'string' ? referenceId : '';
  }
  if (typeof value === 'string' || typeof value === 'number') {
    if (field.type === 'date' && typeof value === 'string') return value.slice(0, 10);
    return String(value);
  }
  return field.defaultValue ?? '';
};

export const makeSettingsFormValues = (
  fields: readonly SettingsFormField[],
  record?: SettingsRecord | null,
): SettingsFormValues => Object.fromEntries(
  fields.map((field) => [field.path, toFormValue(field, record ? getValueAtPath(record, field.path) : undefined)]),
);

const setValueAtPath = (target: Record<string, unknown>, path: string, value: unknown) => {
  const segments = path.split('.');
  let current: Record<string, unknown> | unknown[] = target;

  segments.forEach((segment, index) => {
    const isLast = index === segments.length - 1;
    if (isLast) {
      if (Array.isArray(current)) current[Number(segment)] = value;
      else current[segment] = value;
      return;
    }

    const nextIsArray = /^\d+$/.test(segments[index + 1] ?? '');
    if (Array.isArray(current)) {
      const itemIndex = Number(segment);
      const existing = current[itemIndex];
      if (!(Array.isArray(existing) || isObject(existing))) current[itemIndex] = nextIsArray ? [] : {};
      current = current[itemIndex] as Record<string, unknown> | unknown[];
    } else {
      const existing = current[segment];
      if (!(Array.isArray(existing) || isObject(existing))) current[segment] = nextIsArray ? [] : {};
      current = current[segment] as Record<string, unknown> | unknown[];
    }
  });
};

export const makeSettingsUpdateSeed = (record: SettingsRecord, paths: readonly string[]) => {
  const seed: Record<string, unknown> = {};
  paths.forEach((path) => {
    const value = getValueAtPath(record, path);
    if (value !== undefined) setValueAtPath(seed, path, structuredClone(value));
  });
  return seed;
};

const payloadValue = (field: SettingsFormField, value: SettingsFormValue): unknown => {
  if (field.type === 'multi-select' || field.type === 'checkbox-group') return Array.isArray(value) ? value : [];
  if (field.type === 'number') return value === '' ? field.required ? undefined : null : Number(value);
  if ((field.type === 'select' || field.type === 'image' || field.type === 'date')
    && typeof value === 'string' && !value.trim()) {
    return field.required ? undefined : null;
  }
  if (typeof value !== 'string') return undefined;
  if (isPhoneFieldName(field.path)) {
    const phone = normalizePhoneInput(value);
    return phone ? phone : field.required ? undefined : null;
  }
  return value.trim() ? value.trim() : field.required ? undefined : null;
};

export const buildSettingsPayload = (
  fields: readonly SettingsFormField[],
  values: SettingsFormValues,
  seed: Record<string, unknown> = {},
) => {
  const payload = structuredClone(seed);
  fields.forEach((field) => {
    const value = values[field.path];
    if (value === undefined) return;
    const parsed = payloadValue(field, value);
    if (parsed !== undefined) setValueAtPath(payload, field.path, parsed);
  });
  return payload;
};

export const validateSettingsForm = (fields: readonly SettingsFormField[], values: SettingsFormValues) => {
  const errors: Record<string, string> = {};
  fields.forEach((field) => {
    const value = values[field.path];
    const empty = value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
    if (field.required && empty) {
      errors[field.path] = `${field.label} is required.`;
      return;
    }
    if (isPhoneFieldName(field.path) && typeof value === 'string' && value.trim() && !/^\d{10}$/.test(value.trim())) {
      errors[field.path] = `${field.label} must be a 10 digit phone number.`;
      return;
    }
    if (field.type === 'number' && typeof value === 'string' && value !== '') {
      const numberValue = Number(value);
      if (!Number.isFinite(numberValue)) errors[field.path] = `${field.label} must be a number.`;
      else if (field.min !== undefined && numberValue < field.min) errors[field.path] = `${field.label} must be at least ${field.min}.`;
      else if (field.max !== undefined && numberValue > field.max) errors[field.path] = `${field.label} must be at most ${field.max}.`;
      else if (field.minimumValuePath) {
        const minimumValue = values[field.minimumValuePath];
        if (typeof minimumValue === 'string' && minimumValue !== '' && numberValue < Number(minimumValue)) {
          errors[field.path] = `${field.label} must be greater than or equal to the minimum age.`;
        }
      }
    }
  });
  return errors;
};

const referenceLabel = (value: Record<string, unknown>) => {
  const name = value.label ?? value.name ?? value.departmentName ?? value.referralName ?? value.sampleName ?? value.tpaName ?? value.vialName ?? value.bedName ?? value.diagnosisName ?? value.wardName ?? value.packageName ?? value.headerName ?? value.displayName ?? value.testName ?? value.hospitalName;
  const code = value.code ?? value.testCode ?? value.sectionCode;
  if (typeof name === 'string' && typeof code === 'string') return `${name} (${code})`;
  if (typeof name === 'string') return name;
  if (typeof code === 'string') return code;
  return typeof value.id === 'string' ? value.id : 'Linked record';
};

export const formatSettingsValue = (value: unknown, format: SettingsColumnFormat = 'text') => {
  if (value === undefined || value === null || value === '') return '-';
  if (format === 'boolean') return value ? 'Yes' : 'No';
  if (format === 'currency' && typeof value === 'number') {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(value);
  }
  if (format === 'number' && typeof value === 'number') return new Intl.NumberFormat('en-IN').format(value);
  if (format === 'date' && (typeof value === 'string' || value instanceof Date)) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : formatDate(date);
  }
  if (format === 'doctor-name' && typeof value === 'string') return doctorDisplayName(value);
  if (Array.isArray(value)) {
    return value.map((item) => isObject(item) ? referenceLabel(item) : String(item)).join(', ') || '-';
  }
  if (isObject(value)) return referenceLabel(value);
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return String(value);
  return '-';
};

export const settingsRecordLabel = (record: SettingsRecord) => {
  const label = record.name ?? record.employeeId ?? record.departmentName ?? record.referralName ?? record.notice ?? record.sampleName ?? record.tpaName ?? record.vialName ?? record.bedName ?? record.diagnosisName ?? record.wardName ?? record.packageName ?? record.headerName ?? record.displayName ?? record.testName ?? record.code ?? record.sectionCode;
  if (typeof label !== 'string') return 'this record';
  return label === record.notice ? stripHtml(label) || 'this record' : label;
};

export const lookupRecordLabel = (record: SettingsRecord) => {
  const name = record.label ?? record.name ?? record.departmentName ?? record.referralName ?? record.sampleName ?? record.tpaName ?? record.vialName ?? record.bedName ?? record.diagnosisName ?? record.wardName ?? record.packageName ?? record.headerName ?? record.testName ?? record.displayName;
  const code = record.code ?? record.testCode;
  if (typeof name === 'string' && typeof code === 'string') return `${code} - ${name}`;
  if (typeof name === 'string') return name;
  if (typeof code === 'string') return code;
  return record.id;
};

export const getReferenceId = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (!isObject(value)) return '';
  const id = value.id ?? value._id;
  return typeof id === 'string' ? id : '';
};

export const calculateTdsPreview = (paymentAmount: number, tdsPercent: number) => {
  const rawTds = paymentAmount * tdsPercent / 100;
  return Math.round((rawTds + Number.EPSILON) * 100) / 100;
};

export const clampSettingsPage = (page: number, totalPages: number) =>
  Math.min(Math.max(page, 1), Math.max(totalPages, 1));

export const buildTdsApplyChanges = (changes: Readonly<Record<string, boolean>>): TdsApplyChange[] =>
  Object.entries(changes).map(([id, applied]) => ({ id, applied }));
